// frontend/src/pages/perfis/PerfisListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function PerfisListPage() {
  const { loading: authLoading, hasPermission } = useAuth();
  const canManage = hasPermission('config.perfis');

  const [perfis, setPerfis] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    if (!canManage) {
      setLoading(false);
      setError({ kind: 'permission' });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchPerfis = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/perfis', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setPerfis(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchPerfis();
    return () => controller.abort();
  }, [authLoading, canManage, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : perfis.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (id === 1) {
      alert('O perfil "Administrador" não pode ser desativado.');
      return;
    }
    if (!window.confirm('Tem certeza de que deseja desativar este perfil?')) return;

    try {
      await api.delete(`/perfis/${id}`);
      setPerfis((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else if (err?.response?.data?.message) {
        alert(err.response.data.message);
      } else {
        alert('Ocorreu um erro ao desativar o perfil.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Tem certeza de que deseja reativar este perfil?')) return;

    try {
      await api.patch(`/perfis/${id}/restore`);
      setPerfis((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao reativar o perfil.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="perfis-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="perfis-page">
        <LoadingSpinner text="Carregando perfis..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="perfis-page">
          <PermissionDeniedAlert
            requiredPermission="config.perfis"
            action="visualizar ou gerenciar perfis"
          />
        </div>
      );
    }
    return (
      <div className="perfis-page">
        <AlertException error={error} fallback="Não foi possível carregar os perfis." />
      </div>
    );
  }

  return (
    <div className="perfis-page">
      <div className="content-header">
        <h1 className="content-title">Perfis</h1>
        <p className="content-subtitle">Lista de perfis de usuário cadastrados no sistema</p>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="list-toolbar perfis-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="perfis-filters-group d-flex align-items-center gap-3">
              <div className="input-group perfis-search-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome ou tipo de acesso..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="perfis-status-filter d-flex align-items-center gap-2">
                <label htmlFor="perfisStatusFilter" className="mb-0 perfis-status-label">Filtrar por:</label>
                <select
                  id="perfisStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar perfis por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="perfis-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            <Link to="/painel/perfis/novo" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Novo Perfil
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo de Acesso</th>
                  <th>Data de Criação</th>
                  <th>Status</th>
                  <th style={{ minWidth: '200px', textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {perfis.length > 0 ? (
                  perfis.map((perfil) => {
                    const isInactive = Boolean(perfil.deleted_at);
                    const isProtected = perfil.id === 1;

                    return (
                      <tr key={perfil.id}>
                        <td data-label="Nome">{perfil.nome}</td>
                        <td data-label="Tipo de Acesso">{perfil.tipo_acesso}</td>
                        <td data-label="Data de Criação">{perfil.created_at ? String(perfil.created_at).split(' ')[0] : '-'}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions perfis-table-actions d-flex justify-content-center gap-2" data-label="Ações">
                          {!isInactive && (
                            <Link
                              to={`/painel/perfis/${perfil.id}/dashboard-config`}
                              className="btn btn-sm btn-outline-secondary"
                              title="Configurar visualização do Painel"
                            >
                              <i className="bi bi-layout-sidebar-inset"></i>
                            </Link>
                          )}

                          <Link
                            to={`/painel/perfis/${perfil.id}/visualizar`}
                            className="btn btn-sm btn-outline-primary"
                            title="Visualizar Perfil"
                          >
                            <i className="bi bi-eye"></i>
                          </Link>

                          {!isInactive && (
                            <Link
                              to={`/painel/perfis/${perfil.id}/permissoes`}
                              className="btn btn-sm btn-outline-info"
                              title="Gerenciar Permissões"
                            >
                              <i className="bi bi-shield-lock"></i>
                            </Link>
                          )}

                          {!isInactive && (
                            <Link
                              to={`/painel/perfis/${perfil.id}/editar`}
                              className={`btn btn-sm btn-outline-warning ${isProtected ? 'disabled' : ''}`}
                              title={isProtected ? 'Este perfil não pode ser editado' : 'Editar'}
                              onClick={(e) => isProtected && e.preventDefault()}
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {!isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title={isProtected ? 'Este perfil não pode ser desativado' : 'Desativar'}
                              onClick={() => handleDelete(perfil.id)}
                              disabled={isProtected}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}

                          {isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(perfil.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">Nenhum perfil encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="perfis-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerfisListPage;
